import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LogOut, Menu } from "lucide-react";
import EventSurgeBanner from "@/components/EventSurgeBanner";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const dashboardPath =
    user?.role === "hotel" ? "/dashboard/hotel" : user?.role === "driver" ? "/dashboard/driver" : user?.role === "admin" ? "/admin" : "/";

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl pt-[env(safe-area-inset-top)]" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 sm:px-6 lg:px-10">
        <Link to="/welcome" className="flex items-center gap-2.5" data-testid="nav-logo">
          <div className="relative h-9 w-9 rounded-2xl bg-primary grid place-items-center rotate-[-4deg] shadow-md cartoon-border">
            <span className="font-[Outfit] text-lg font-bold text-primary-foreground">P</span>
            <span className="absolute -right-1 -bottom-1 h-3 w-3 rounded-full bg-accent ring-2 ring-background" />
          </div>
          <span className="font-[Outfit] text-xl tracking-tight font-semibold">Price</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/" end className={({isActive}) => `text-sm transition-colors ${isActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"}`} data-testid="nav-home">
            Accueil
          </NavLink>
          {!user && (
            <>
              <NavLink to="/register/client" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-register-client">
                Covoiturage
              </NavLink>
              <NavLink to="/register/driver" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-register-driver">
                Chauffeurs
              </NavLink>
            </>
          )}
          {user && (
            <NavLink to={dashboardPath} className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-dashboard">
              Tableau de bord
            </NavLink>
          )}
          {user?.role === "driver" && (
            <NavLink to="/dashboard/driver/history" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-driver-history">
              Historique
            </NavLink>
          )}
          {user?.role === "hotel" && (
            <NavLink to="/dashboard/hotel/history" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-hotel-history">
              Historique
            </NavLink>
          )}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-2 sm:gap-3 md:flex">
          {!user ? (
            <>
              <Link to="/register" data-testid="nav-cta">
                <Button className="rounded-full bg-primary px-5 text-sm hover:bg-primary/90 font-semibold shadow-md">
                  Rejoindre
                </Button>
              </Link>
              <Link to="/login" data-testid="nav-login">
                <Button variant="outline" className="rounded-full px-5 text-sm border-primary/30 text-primary hover:bg-primary/5 hover:text-primary">
                  Connexion
                </Button>
              </Link>
            </>
          ) : (
            <Button variant="ghost" onClick={handleLogout} className="gap-2 text-sm" data-testid="nav-logout">
              <LogOut className="h-4 w-4" />
              <span>Se déconnecter</span>
            </Button>
          )}
        </div>

        {/* Mobile : hamburger */}
        <div className="flex items-center gap-2 md:hidden">
          {!user && (
            <Link to="/login" data-testid="nav-login-mobile">
              <Button variant="outline" size="sm" className="rounded-full border-primary/30 px-3 text-xs text-primary hover:bg-primary/5 hover:text-primary">
                Connexion
              </Button>
            </Link>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Ouvrir le menu"
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-foreground transition-colors hover:bg-secondary"
                data-testid="nav-menu-trigger"
              >
                <Menu className="h-4 w-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] p-0">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex h-full flex-col">
                <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
                  <div className="relative h-9 w-9 rounded-2xl bg-primary grid place-items-center rotate-[-4deg] shadow-md cartoon-border">
                    <span className="font-[Outfit] text-lg font-bold text-primary-foreground">P</span>
                    <span className="absolute -right-1 -bottom-1 h-3 w-3 rounded-full bg-accent ring-2 ring-background" />
                  </div>
                  <span className="font-[Outfit] text-xl tracking-tight font-semibold">Price</span>
                </div>

                <nav className="flex-1 px-3 py-4" data-testid="nav-mobile-menu">
                  <MobileLink to="/" onClick={() => setOpen(false)} testId="nav-mobile-home">Accueil</MobileLink>
                  {!user && (
                    <>
                      <MobileLink to="/register/client" onClick={() => setOpen(false)} testId="nav-mobile-client">Espace Covoiturage</MobileLink>
                      <MobileLink to="/register/driver" onClick={() => setOpen(false)} testId="nav-mobile-driver">Devenir chauffeur</MobileLink>
                      <MobileLink to="/login" onClick={() => setOpen(false)} testId="nav-mobile-login">Se connecter</MobileLink>
                    </>
                  )}
                  {user && (
                    <MobileLink to={dashboardPath} onClick={() => setOpen(false)} testId="nav-mobile-dash">Tableau de bord</MobileLink>
                  )}
                  {user?.role === "driver" && (
                    <MobileLink to="/dashboard/driver/history" onClick={() => setOpen(false)} testId="nav-mobile-driver-history">Historique</MobileLink>
                  )}
                  {user?.role === "hotel" && (
                    <MobileLink to="/dashboard/hotel/history" onClick={() => setOpen(false)} testId="nav-mobile-hotel-history">Historique</MobileLink>
                  )}
                </nav>

                <div className="border-t border-border p-4">
                  {!user ? (
                    <Link to="/register" onClick={() => setOpen(false)} data-testid="nav-mobile-cta" className="block">
                      <Button className="w-full rounded-full bg-primary hover:bg-primary/90 font-semibold shadow-md">Rejoindre Price</Button>
                    </Link>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleLogout}
                      className="w-full gap-2 rounded-full"
                      data-testid="nav-mobile-logout"
                    >
                      <LogOut className="h-4 w-4" /> Se déconnecter
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {user && <EventSurgeBanner />}
    </header>
  );
}

function MobileLink({ to, children, onClick, testId }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        `block rounded-lg px-3 py-3 text-[15px] transition-colors ${
          isActive ? "bg-primary/10 font-medium text-primary" : "text-foreground/80 hover:bg-secondary"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
